// Mobile Order Detail Page
// Mobile version (<768px)

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orderApi } from '@/services/orderApi';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav, { type NavItem } from '@/components/mobile/MobileBottomNav';
import type { Order, OrderItem } from '@/types';
import styles from './MobileOrderDetailPage.module.css';

const STATUS_LABELS: Record<string, string> = {
  pending: '待處理',
  processing: '處理中',
  shipped: '已出貨',
  delivered: '已送達',
  cancelled: '已取消',
  failed: '失敗',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#ff9800',
  processing: '#2196f3',
  shipped: '#9c27b0',
  delivered: '#4caf50',
  cancelled: '#f44336',
  failed: '#f44336',
};

export default function MobileOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await orderApi.getOrderById(id);
        setOrder(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '載入訂單失敗');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrder();
  }, [id]);

  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCancelOrder = async () => {
    if (!order || !confirm('確定要取消此訂單嗎？')) {
      return;
    }

    setIsCancelling(true);
    setError(null);

    try {
      const response = await orderApi.cancelOrder(order.id);
      setOrder(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '取消訂單失敗');
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.detailPage}>
        <div className={styles.loading}>載入中...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className={styles.detailPage}>
        <MobileHeader
          title="訂單詳情"
          showBack
          onBack={() => navigate('/orders')}
        />
        <div className={styles.error}>{error || '找不到訂單'}</div>
      </div>
    );
  }

  // Nav items for bottom navigation
  const navItems: NavItem[] = [
    {
      id: 'home',
      label: '首頁',
      path: '/',
      icon: '🏠',
    },
    {
      id: 'ads',
      label: '廣告',
      path: '/ads',
      icon: '🛒',
    },
    {
      id: 'wish',
      label: '願望',
      path: '/wishes',
      icon: '⭐',
    },
    {
      id: 'profile',
      label: '會員',
      path: '/member',
      icon: '👤',
    },
  ];

  return (
    <div className={styles.detailPage}>
      <MobileHeader
        title="訂單詳情"
        showBack
        onBack={() => navigate('/orders')}
      />

      {/* Order Number Card */}
      <div className={styles.orderCard}>
        <div className={styles.orderNumber}>{order.orderNumber}</div>
        <div className={styles.orderMeta}>
          <span className={styles.orderDate}>{formatDate(order.createdAt)}</span>
          <span
            className={styles.orderStatus}
            style={{ color: STATUS_COLORS[order.status] || '#666' }}
          >
            {STATUS_LABELS[order.status] || order.status}
          </span>
        </div>
      </div>

      {/* Order Items */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>訂單商品</h2>
        <div className={styles.itemsList}>
          {order.items.map((item: OrderItem) => (
            <div key={item.id} className={styles.item}>
              <div className={styles.itemImage}>
                {item.productSnapshot.featuredImageUrl ? (
                  <img src={item.productSnapshot.featuredImageUrl} alt={item.productSnapshot.title} />
                ) : (
                  <div className={styles.imagePlaceholder} />
                )}
              </div>
              <div className={styles.itemInfo}>
                <span className={styles.itemTitle}>{item.productSnapshot.title}</span>
                {item.variantAttributes && (
                  <span className={styles.itemVariant}>
                    {Object.entries(item.variantAttributes)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(', ')}
                  </span>
                )}
                <span className={styles.itemMeta}>
                  {formatPrice(item.price)} × {item.quantity}
                </span>
              </div>
              <span className={styles.itemSubtotal}>{formatPrice(item.subtotal)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recipient Info */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>收件人資訊</h2>
        <div className={styles.infoCard}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>收件人</span>
            <span className={styles.infoValue}>{order.recipientName}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>聯絡電話</span>
            <span className={styles.infoValue}>{order.recipientPhone}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>配送地址</span>
            <span className={styles.infoValue}>{order.recipientAddress}</span>
          </div>
          {order.notes && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>備註</span>
              <span className={styles.infoValue}>{order.notes}</span>
            </div>
          )}
        </div>
      </div>

      {/* Order Summary */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>訂單金額</h2>
        <div className={styles.summaryCard}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>商品小計</span>
            <span className={styles.summaryValue}>{formatPrice(order.subtotal)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>運費</span>
            <span className={styles.summaryValue}>
              {order.shippingFee === 0 ? '免運' : formatPrice(order.shippingFee)}
            </span>
          </div>
          {order.discount > 0 && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>折扣</span>
              <span className={styles.summaryValue}>-{formatPrice(order.discount)}</span>
            </div>
          )}
          <div className={`${styles.summaryRow} ${styles.totalRow}`}>
            <span className={styles.totalLabel}>總計</span>
            <span className={styles.totalValue}>{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div className={styles.paymentCard}>
        <span className={styles.paymentLabel}>付款狀態</span>
        <span
          className={styles.paymentValue}
          style={{
            color: order.paymentStatus === 'paid' ? '#4caf50' : '#ff9800'
          }}
        >
          {order.paymentStatus === 'paid' ? '已付款' : '待付款'}
        </span>
      </div>

      {/* Cancel Button */}
      {order.status === 'pending' && (
        <div className={styles.actions}>
          <button
            onClick={handleCancelOrder}
            disabled={isCancelling}
            className={styles.cancelButton}
          >
            {isCancelling ? '取消中...' : '取消訂單'}
          </button>
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav items={navItems} />
    </div>
  );
}
